/**
 * AuthKit SlotFills for the commerce portal login screen.
 *
 * Enqueued by SLW\CommercePortal\Auth\Controller::enqueueLegacyLoginLink
 * on `workos_authkit_enqueue_assets` for every login profile.
 *
 * - Login notice: workos.authkit.beforeHeader, visually reordered under
 *   the logo (AuthKit has no between-logo-and-card slot). Not dismissible
 *   — people who cannot sign in need to keep seeing it. HTML is sanitized
 *   server-side via wp_kses_post.
 * - Legacy link: workos.authkit.afterFooter, default profile, pick step
 *   only.
 */

import { Fill } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { registerPlugin } from '@wordpress/plugins';
import type { PortalNotice, PortalNoticeSeverity } from '@/types/portal-data';

import '@styles/login-notice.css';
import './workos-login-extender.css';

interface LegacyLoginData {
	legacyLoginUrl?: string;
	notice?: PortalNotice | null;
}

declare global {
	interface Window {
		slwLegacyLoginExtender?: LegacyLoginData;
	}
}

const LEGACY_URL = window.slwLegacyLoginExtender?.legacyLoginUrl ?? '';

const ASSERTIVE_SEVERITIES: ReadonlySet< PortalNoticeSeverity > = new Set( [
	'warning',
	'error',
] );

export function LoginNotice( { notice }: { notice: PortalNotice } ) {
	if ( ! notice.message ) {
		return null;
	}

	const role = ASSERTIVE_SEVERITIES.has( notice.severity )
		? 'alert'
		: 'status';

	return (
		<div
			role={ role }
			className={ `slw-login-notice slw-login-notice--${ notice.severity }` }
		>
			<div
				className="slw-login-notice__message"
				dangerouslySetInnerHTML={ { __html: notice.message } }
			/>
		</div>
	);
}

function LegacyLink( { step }: { step: string } ) {
	if ( ! LEGACY_URL || step !== 'pick' ) {
		return null;
	}
	return (
		<p className="wa-footer">
			{ __( 'Legacy customer?', 'commerce-portal' ) }{ ' ' }
			<a className="slw-legacy-login-link" href={ LEGACY_URL }>
				{ __( 'Sign in here', 'commerce-portal' ) }
			</a>
		</p>
	);
}

registerPlugin( 'slw-commerce-portal-login-notice', {
	scope: 'workos-authkit',
	render: () => (
		<Fill name="workos.authkit.beforeHeader">
			{ () => {
				const notice = window.slwLegacyLoginExtender?.notice;
				if ( ! notice || ! notice.message ) {
					return null;
				}
				return <LoginNotice notice={ notice } />;
			} }
		</Fill>
	),
} );

registerPlugin( 'slw-commerce-portal-legacy-login-link', {
	scope: 'workos-authkit',
	render: () => (
		<Fill name="workos.authkit.afterFooter">
			{ ( fillProps: Record< string, unknown > ) => {
				// Validate the slot contract at the third-party boundary: if
				// AuthKit ever changes the fillProps shape, fail closed rather
				// than render "undefined undefined" or worse.
				const { step } = fillProps;
				if ( typeof step !== 'string' ) {
					return null;
				}
				return <LegacyLink step={ step } />;
			} }
		</Fill>
	),
} );
